/*
  v1 에서 그대로 옮겨 온 임시 목 저장소(V2.md 「화면 이관 현황」). 아직 네 곳이 읽는다
  (useMenuSettings · queries · AssetNoticePanel · calendar/queries) — 그 넷이 apiFetch 로 옮겨 가면
  파일째 지운다. 그때까지 lint 가 여기서 서지 않게만 한다.
*/
/* eslint-disable @typescript-eslint/ban-ts-comment, @typescript-eslint/no-explicit-any */
// @ts-nocheck
import type {
  BomiPerformancePost,
  ConstructionTrendPost,
  ConstructionTrendSource,
  Department,
  EmployeeNewsItem,
  InternalGuidelinePost,
  HqCafeteriaMenu,
  HqTaskItem,
  MonthlyMeetingCategory,
  MonthlyMeetingEvent,
  MenuSetting,
  MonthlyMeetingException,
  NoticePost,
  Personnel,
  PersonnelAssignment,
  PersonnelCareer,
  RequestBoardPost,
  Site,
  SiteCafeteriaMenu,
  SiteRestaurant,
  SiteTaskCategory,
  SiteTaskItem,
  TechnicalPost,
  UserRecord,
  Week,
  WeeklyExecutiveNote,
} from '@/lib/types';
import { apiFetch, ApiRequestError } from '@/lib/api/client';
import { env } from '@/lib/env';
import { formatDate, getMondayOfWeek, getWeekLabelInfo } from '@/lib/utils/week';

/**
 * 임시 목 데이터 저장소.
 *
 * 백엔드 API 가 붙기 전까지 이관한 화면이 실제처럼 보이고 동작하도록 브라우저 메모리에 데이터를 둔다.
 * 새로고침하면 아래 시드로 되돌아간다 — 저장되는 곳이 없다.
 *
 * **화면은 이 모듈을 TanStack Query 의 `queryFn` / `mutationFn` 자리에서만 호출한다.**
 * 서버가 붙으면 그 한 줄을 `apiFetch` 로 바꾸는 것으로 끝나고, 화면 코드는 손대지 않는다.
 * 그러려면 여기서 하는 일이 "네트워크 한 번"과 같은 모양이어야 하므로,
 * 목록은 항상 복사본을 돌려주고 지연도 흉내 낸다.
 */

type MockTables = {
  weeks: Week[];
  departments: Department[];
  sites: Site[];
  hqCafeteriaMenus: HqCafeteriaMenu[];
  siteCafeteriaMenus: SiteCafeteriaMenu[];
  siteRestaurants: SiteRestaurant[];
  requestBoardPosts: RequestBoardPost[];
  personnel: Personnel[];
  personnelCareers: PersonnelCareer[];
  personnelAssignments: PersonnelAssignment[];
  noticePosts: NoticePost[];
  /** 자산관리 공지. 같은 표(`board_notice_posts`)의 다른 게시판이라 행 모양은 공지사항과 같다. */
  assetNoticePosts: NoticePost[];
  employeeNewsItems: EmployeeNewsItem[];
  /** 비어 있으면 기본 메뉴 골격이 그대로 쓰인다. 메뉴 관리 화면에서 채워진다. */
  menuSettings: MenuSetting[];
  users: UserRecord[];
  constructionTrendPosts: ConstructionTrendPost[];
  constructionTrendSources: ConstructionTrendSource[];
  internalGuidelinePosts: InternalGuidelinePost[];
  bomiPerformancePosts: BomiPerformancePost[];
  technicalCasePosts: TechnicalPost[];
  newTechnologyPosts: TechnicalPost[];
  hqTaskItems: HqTaskItem[];
  siteTaskItems: SiteTaskItem[];
  weeklyExecutiveNotes: WeeklyExecutiveNote[];
  monthlyMeetingCategories: MonthlyMeetingCategory[];
  monthlyMeetingEvents: MonthlyMeetingEvent[];
  monthlyMeetingExceptions: MonthlyMeetingException[];
  /**
   * 홈 TO DO 위젯의 개인 할일.
   *
   * 목 저장소는 사용자가 한 명뿐인 것으로 본다 — `userId` 로 거르지 않고 전부 돌려준다.
   * 백엔드가 붙으면 세션으로 거르는 일은 서버가 하므로 화면 코드는 그대로 둔다.
   */
};

type TableName = keyof MockTables;
type RowOf<K extends TableName> = MockTables[K][number];

/** 실제 요청처럼 로딩 상태를 한 번 거치게 한다. 너무 길면 개발이 답답해진다. */
const LATENCY_MS = 150;

let idSequence = 0;

function nextId(prefix: string): string {
  idSequence += 1;
  return `${prefix}-${idSequence}`;
}

function settle<T>(value: T): Promise<T> {
  return new Promise((resolve) => {
    window.setTimeout(() => resolve(value), LATENCY_MS);
  });
}

/**
 * 목록 조회를 실제 백엔드로 넘긴 표 → 엔드포인트. 서버가 요청마다 필터·정렬하므로 화면 코드는 그대로 두고
 * 여기서만 API 로 바꾼다({@link mockList} 의 목적 그대로 — "queryFn 한 줄"만 갈아끼운 것과 같다).
 *
 * <b>여기 없는 표는 아직 목이다</b> — 주차 범위·현장 등 인자가 필요한 목록(주간/월간회의·식단·경력)과
 * 메뉴 설정은 화면별로 인자를 실어 옮겨야 해서 다음 단계로 남긴다. 쓰기(insert/update/remove)도 아직 목이다.
 */
const API_LIST_PATHS: Partial<Record<TableName, string>> = {
  weeks: '/v1/weeks',
  departments: '/v1/departments',
  sites: '/v1/sites',
  requestBoardPosts: '/v1/request-posts',
  personnel: '/v1/personnel?includeLeavers=true',
  personnelAssignments: '/v1/assignments',
  noticePosts: '/v1/notices?boardKey=NEWS',
  assetNoticePosts: '/v1/notices?boardKey=ASSET',
  employeeNewsItems: '/v1/employee-news',
  users: '/v1/users',
  constructionTrendPosts: '/v1/trend-posts',
  constructionTrendSources: '/v1/trend-sources',
  internalGuidelinePosts: '/v1/guidelines',
  bomiPerformancePosts: '/v1/performances',
  technicalCasePosts: '/v1/technical-posts?boardKind=CASE',
  newTechnologyPosts: '/v1/technical-posts?boardKind=NEW',
  monthlyMeetingCategories: '/v1/meeting-categories',
  monthlyMeetingEvents: '/v1/meeting-events',
  monthlyMeetingExceptions: '/v1/meeting-events/exceptions',
  hqTaskItems: '/v1/hq-tasks',
  siteTaskItems: '/v1/site-tasks',
  weeklyExecutiveNotes: '/v1/executive-notes',
  hqCafeteriaMenus: '/v1/cafeteria-menus',
  // 현장 주간식단(V298). 표가 생기기 전까지 이 한 줄이 비어 있어서 홈 위젯의 현장 탭이 v1 시절
  // 목을 읽고 있었고 — 목의 `siteId` 는 'site-1' 같은 가짜라 진짜 UUID 와 영영 맞지 않아 —
  // 늘 빈칸이었다(DOC-81 §4).
  siteCafeteriaMenus: '/v1/site-cafeteria-menus',
  siteRestaurants: '/v1/site-restaurants',
  personnelCareers: '/v1/careers',
  menuSettings: '/v1/menu-settings',
};

/**
 * 오프라인이어도 <b>목으로 물러서면 안 되는</b> 표.
 *
 * <p><b>현장.</b> 현장 id 는 화면에 뿌리는 이름이 아니라 <b>서버가 소속 격리(계층2)를 판정하는 열쇠</b>다
 * (`PmisSiteAccess`). 현장 조회(PMIS)의 모든 요청이 `siteId`(UUID)를 싣고 나간다.
 *
 * <p>여기서 데모 현장(`site-2`)으로 물러서면, 서버가 되살아난 뒤에도 화면에는 그 현장이 남는다.
 * 그 상태로 무언가를 저장하면 서버가
 * <code>Cannot deserialize value of type UUID from String "site-2"</code> 로 400 을 낸다 —
 * 그리고 <b>범인은 마지막에 누른 기능처럼 보인다</b>(2026-08-10, 엑셀로 공정 만들기가 그렇게 의심받았다).
 *
 * <p>그래서 현장은 <b>못 읽으면 못 읽었다고 말한다.</b> 빈 목록이 가짜 목록보다 낫다.
 */
const NO_OFFLINE_FALLBACK = new Set<TableName>(['sites']);

/** 서버에 닿지 못했고(오프라인) 개발 중일 때만 목으로 물러선다 — 세션이 목으로 열리는 것과 같은 판정이다. */
function canFallBackOffline(error: unknown, table?: TableName): boolean {
  if (table !== undefined && NO_OFFLINE_FALLBACK.has(table)) return false;
  const unreachable =
    error instanceof TypeError || (error instanceof ApiRequestError && error.apiError === null);
  return unreachable && env.mockSessionWhenOffline;
}

export async function mockList<K extends TableName>(table: K): Promise<MockTables[K]> {
  const path = API_LIST_PATHS[table];
  if (path) {
    try {
      return await apiFetch<MockTables[K]>(path);
    } catch (error) {
      if (canFallBackOffline(error, table)) return [...tables[table]] as MockTables[K];
      throw error;
    }
  }
  return settle([...tables[table]] as MockTables[K]);
}

/**
 * 쓰기를 실제 백엔드로 넘긴 표 → 기준 경로(POST=생성, PUT/{id}=수정, DELETE/{id}=삭제).
 *
 * <b>전체 객체를 보내는 표준 CRUD 만 여기 둔다.</b> 부분 패치(`{sortOrder}`·`{content}`·상태 토글)나
 * 전용 엔드포인트(order·bulk·answer·meeting-color·exceptions)를 쓰는 도메인은 아직 목이다 — 그쪽은 화면별로 옮긴다.
 */
const API_WRITE_PATHS: Partial<Record<TableName, string>> = {
  noticePosts: '/v1/notices',
  assetNoticePosts: '/v1/notices',
  employeeNewsItems: '/v1/employee-news',
  internalGuidelinePosts: '/v1/guidelines',
  personnel: '/v1/personnel',
  departments: '/v1/departments',
  sites: '/v1/sites',
  users: '/v1/users',
  requestBoardPosts: '/v1/request-posts',
  constructionTrendPosts: '/v1/trend-posts',
  constructionTrendSources: '/v1/trend-sources',
  siteRestaurants: '/v1/site-restaurants',
  personnelCareers: '/v1/careers',
  personnelAssignments: '/v1/assignments',
  bomiPerformancePosts: '/v1/performances',
  technicalCasePosts: '/v1/technical-posts',
  newTechnologyPosts: '/v1/technical-posts',
  monthlyMeetingCategories: '/v1/meeting-categories',
  monthlyMeetingEvents: '/v1/meeting-events',
  hqTaskItems: '/v1/hq-tasks',
  siteTaskItems: '/v1/site-tasks',
};

/** 정렬 전용 엔드포인트({base}/order). 화면이 순서대로 넘긴 id 목록으로 서버가 sort_order 를 다시 매긴다. */
export async function dbReorder<K extends TableName>(table: K, ids: string[]): Promise<void> {
  const path = API_WRITE_PATHS[table];
  if (!path) return;
  try {
    await apiFetch<unknown>(`${path}/order`, { method: 'PUT', body: { ids } });
  } catch (error) {
    if (!canFallBackOffline(error, table)) throw error; // 오프라인이면 순서 변경은 목에서 무시(다음 조회 때 원복)
  }
}

/** 전용 엔드포인트(답변·발행·현장색·업서트·bulk 등) 호출용. 오프라인이면 null 을 돌려 화면이 조용히 넘어가게 한다. */
export async function dbApi<T>(path: string, method: 'POST' | 'PUT' | 'DELETE', body?: unknown): Promise<T | null> {
  try {
    return await apiFetch<T>(path, body === undefined ? { method } : { method, body });
  } catch (error) {
    if (canFallBackOffline(error)) return null;
    throw error;
  }
}

export async function dbInsert<K extends TableName>(table: K, row: Omit<RowOf<K>, 'id'>): Promise<RowOf<K>> {
  const path = API_WRITE_PATHS[table];
  if (path) {
    try {
      return await apiFetch<RowOf<K>>(path, { method: 'POST', body: row });
    } catch (error) {
      if (canFallBackOffline(error, table)) return mockInsert(table, row);
      throw error;
    }
  }
  return mockInsert(table, row);
}

/**
 * 수정. 백엔드의 PUT 은 <b>전체 교체</b>라 부분 패치를 그대로 보내면 나머지 필드가 지워진다.
 * 그래서 현재 행을 목록에서 읽어 패치를 얹은 <b>전체 객체</b>로 PUT 한다(read-modify-write).
 * 전용 액션(답변·발행·활성토글·업서트 등)은 각 화면이 {@link dbApi} 로 따로 부른다.
 */
export async function dbUpdate<K extends TableName>(table: K, id: string, patch: Partial<RowOf<K>>): Promise<RowOf<K>> {
  const path = API_WRITE_PATHS[table];
  if (path) {
    try {
      const listPath = API_LIST_PATHS[table];
      let body: unknown = patch;
      if (listPath) {
        const rows = await apiFetch<Array<RowOf<K> & { id: string }>>(listPath);
        const current = rows.find((row) => row.id === id);
        if (current) body = { ...current, ...patch };
      }
      return await apiFetch<RowOf<K>>(`${path}/${id}`, { method: 'PUT', body });
    } catch (error) {
      if (canFallBackOffline(error, table)) return mockUpdate(table, id, patch);
      throw error;
    }
  }
  return mockUpdate(table, id, patch);
}

export async function dbRemove<K extends TableName>(table: K, id: string): Promise<void> {
  const path = API_WRITE_PATHS[table];
  if (path) {
    try {
      await apiFetch<unknown>(`${path}/${id}`, { method: 'DELETE' });
      return;
    } catch (error) {
      if (canFallBackOffline(error, table)) return mockRemove(table, id);
      throw error;
    }
  }
  return mockRemove(table, id);
}

export function mockInsert<K extends TableName>(table: K, row: Omit<RowOf<K>, 'id'>): Promise<RowOf<K>> {
  const created = { ...row, id: nextId(table) } as RowOf<K>;
  (tables[table] as RowOf<K>[]).push(created);
  return settle(created);
}

export function mockUpdate<K extends TableName>(
  table: K,
  id: string,
  patch: Partial<RowOf<K>>,
): Promise<RowOf<K>> {
  const rows = tables[table] as RowOf<K>[];
  const index = rows.findIndex((row) => row.id === id);
  if (index < 0) return Promise.reject(new Error(`${table}에 ${id} 가 없습니다.`));

  const updated = { ...rows[index], ...patch } as RowOf<K>;
  rows[index] = updated;
  return settle(updated);
}

/**
 * 지금 메모리에 있는 전체 데이터. 데이터 백업 화면이 쓴다.
 *
 * 진짜 백업은 서버가 만들어야 한다 — 브라우저는 자기가 읽을 수 있는 것만 담을 수 있으므로
 * 권한 밖의 데이터가 빠진 "반쪽 백업"이 나온다. 백엔드에 백업 엔드포인트가 생기면 이 함수는 사라진다.
 */
export function mockSnapshot(): Promise<MockTables> {
  return settle(structuredClone(tables));
}

export function mockRemove<K extends TableName>(table: K, id: string): Promise<void> {
  const rows = tables[table] as RowOf<K>[];
  const index = rows.findIndex((row) => row.id === id);
  if (index >= 0) rows.splice(index, 1);
  return settle(undefined);
}

// ── 시드 ──────────────────────────────────────────────────────────────────────

/**
 * 최근 `count` 개 주차. 오늘이 속한 주가 첫 항목이다.
 *
 * 날짜를 고정값으로 박아 두면 화면을 열 때마다 "지난 주차만 있는" 상태가 되므로
 * 오늘을 기준으로 만든다. 라벨 규칙은 실제 계산기를 그대로 쓴다 — 시드가 규칙을 어기면
 * 주차 화면을 보고도 규칙 버그를 못 찾는다.
 */
function seedWeeks(count: number): Week[] {
  const thisMonday = getMondayOfWeek(new Date());

  return Array.from({ length: count }, (_, index) => {
    const monday = new Date(thisMonday);
    monday.setDate(thisMonday.getDate() - index * 7);
    const sunday = new Date(monday);
    sunday.setDate(monday.getDate() + 6);

    const info = getWeekLabelInfo(monday);
    return {
      id: `week-${formatDate(monday)}`,
      year: info.year,
      month: info.month,
      weekNumber: info.weekNumber,
      startDate: formatDate(monday),
      endDate: formatDate(sunday),
      label: `${info.month}월 ${info.weekNumber}주차`,
    };
  });
}

/** 오늘로부터 `daysAgo` 일 전. 게시글 작성일처럼 "최근"이어야 자연스러운 값에 쓴다. */
function daysAgo(days: number): string {
  const date = new Date();
  date.setDate(date.getDate() - days);
  return date.toISOString();
}

const SEED_WEEKS = seedWeeks(8);
/** 오늘이 속한 주. 식단·업무 시드를 이 주에 붙여 화면을 열자마자 내용이 보이게 한다. */
const CURRENT_WEEK = SEED_WEEKS[0] as Week;
/** 직전 주. "직전 주차 불러오기"가 동작하려면 지난주에도 내용이 있어야 한다. */
const PREV_WEEK = SEED_WEEKS[1] as Week;

/**
 * 현장 시드 — 이 표 자체는 화면에 안 나오지만(아래 `sites:` 의 주석), <b>개발계 목 데이터가</b>
 * 서버에서 현장을 못 받았을 때 이 이름들로 대신 그린다({@code mock/officeDashboard.ts}).
 */
export const SEED_SITES: Site[] = [
  site('site-1', '청주 반도체 공장 신축', '국내진행현장', '진행', 1, {
    clientName: '(주)한빛반도체',
    siteAddress: '충청북도 청주시 흥덕구 오송읍',
    facilityType: '공장',
    floorCount: '지하 1층 / 지상 4층',
    grossAreaSqm: 48200,
    contractAmount: '82,400백만원',
    meetingColor: '#0ea5e9',
  }),
  site('site-2', '성남 판교 오피스 리모델링', '국내진행현장', '진행', 2, {
    clientName: '판교테크노밸리자산운용',
    siteAddress: '경기도 성남시 분당구 삼평동',
    facilityType: '업무시설',
    floorCount: '지상 12층',
    grossAreaSqm: 21750,
    contractAmount: '19,800백만원',
    meetingColor: '#8b5cf6',
  }),
  site('site-3', '평택 물류센터 신축', '국내계약현장', '진행', 3, {
    clientName: '대한로지스틱스',
    siteAddress: '경기도 평택시 청북읍',
    facilityType: '물류시설',
    floorCount: '지상 5층',
    grossAreaSqm: 63400,
    contractAmount: '54,100백만원',
    meetingColor: '#22c55e',
  }),
  site('site-4', '하노이 산업단지 조성', '해외진행현장', '진행', 4, {
    clientName: 'VN Industrial Park JSC',
    siteAddress: 'Hanoi, Vietnam',
    facilityType: '산업단지',
    contractAmount: 'USD 41,200천',
    meetingColor: '#f97316',
  }),
  site('site-5', '대전 연구동 증축', '국내진행현장', '준공', 5, {
    clientName: '한국기초과학연구원',
    siteAddress: '대전광역시 유성구 과학로',
    facilityType: '연구시설',
    floorCount: '지상 6층',
    grossAreaSqm: 12600,
    contractAmount: '14,300백만원',
  }),
  site('site-6', '인천 항만 배후단지 창고', '국내진행현장', '준공', 6, {
    clientName: '인천항만공사',
    siteAddress: '인천광역시 중구 항동',
    facilityType: '창고시설',
    grossAreaSqm: 8900,
    contractAmount: '7,600백만원',
  }),
  site('site-7', '자카르타 복합시설 검토', '해외검토현장', '미진행', 7, {
    clientName: 'PT Nusantara Development',
    siteAddress: 'Jakarta, Indonesia',
    facilityType: '복합시설',
  }),
];

const SEED_PERSONNEL: Personnel[] = [
  person('p-1', '김도현', '부장', '현장대리인', '건축', '청주 반도체 공장 신축', '1979-03-14', '2008-04-01', '특급기술자 | 건축시공'),
  person('p-2', '이서준', '차장', '공무팀장', '건축', '본사 공무팀', '1984-08-22', '2012-01-16', '고급기술자 | 건축시공'),
  person('p-3', '박민수', '과장', '현장대리인', '토목', '평택 물류센터 신축', '1988-11-05', '2015-03-02', '중급기술자 | 토목시공'),
  person('p-4', '최유진', '대리', '설계담당', '건축', '본사 설계팀', '1992-06-30', '2019-09-01', '초급기술자 | 건축설계'),
  person('p-5', '정한결', '부장', '안전보건관리책임자', '안전', '본사 안전관리팀', '1977-01-09', '2006-07-03', '특급기술자 | 건설안전'),
  person('p-6', '한지우', '차장', '전기팀장', '전기', '성남 판교 오피스 리모델링', '1985-05-18', '2011-11-14', '고급기술자 | 전기공사'),
  person('p-7', '오세영', '과장', '기계담당', '기계', '청주 반도체 공장 신축', '1989-09-27', '2016-02-15', '중급기술자 | 기계설비'),
  person('p-8', '문가람', '사원', '공무담당', '건축', '하노이 산업단지 조성', '1996-12-11', '2023-01-09', '초급기술자 | 건축시공'),
];

const tables: MockTables = {
  weeks: SEED_WEEKS,

  departments: [
    department('dept-1', '경영지원팀', 1),
    department('dept-2', '공사팀', 2),
    department('dept-3', '공무팀', 3),
    department('dept-4', '설계팀', 4),
    department('dept-5', '안전관리팀', 5),
    department('dept-6', '견적팀', 6),
    department('dept-7', '해외사업팀', 7, false),
  ],

  // ⚠ 이 시드는 <b>화면에 나오지 않는다.</b> 현장은 오프라인 폴백이 금지돼 있다
  //   ({@link NO_OFFLINE_FALLBACK}) — 서버에 못 닿으면 빈 목록이다.
  //   여기 남겨 둔 것은 다른 목 시드(주간회의 업무·현장 식당)가 이 id 들을 참조하기 때문이다.
  sites: SEED_SITES,

  hqCafeteriaMenus: [
    {
      id: 'hq-menu-1',
      weekStart: CURRENT_WEEK.startDate,
      // 월~일 7줄. 한 줄이 하루이고, 한 줄 안의 반찬은 `/` 로 나눈다(식당안내 화면과 같은 형식).
      menuText: [
        '쇠고기미역국 / 제육볶음 / 계란찜 / 배추김치',
        '김치찌개 / 고등어구이 / 시금치나물 / 깍두기',
        '순두부찌개 / 돈까스 / 마카로니샐러드 / 배추김치',
        '설렁탕 / 오징어볶음 / 감자조림 / 총각김치',
        '된장찌개 / 닭갈비 / 어묵볶음 / 배추김치',
        '',
        '',
      ].join('\n'),
      updatedAt: daysAgo(2),
    },
  ],

  siteCafeteriaMenus: [
    {
      id: 'site-menu-1',
      siteId: 'site-1',
      weekStart: CURRENT_WEEK.startDate,
      menuText: [
        '북엇국 / 불고기 / 콩나물무침 / 배추김치',
        '육개장 / 코다리조림 / 도라지무침 / 깍두기',
        '미역국 / 제육볶음 / 계란말이 / 배추김치',
        '김치찌개 / 갈치구이 / 무생채 / 총각김치',
        '떡국 / 탕수육 / 시금치나물 / 배추김치',
        '',
        '',
      ].join('\n'),
      updatedAt: daysAgo(1),
    },
    {
      id: 'site-menu-2',
      siteId: 'site-2',
      weekStart: CURRENT_WEEK.startDate,
      menuText: [
        '순두부찌개 / 닭볶음탕 / 애호박볶음 / 배추김치',
        '된장찌개 / 삼치구이 / 고사리나물 / 깍두기',
        '소고기무국 / 돈까스 / 감자샐러드 / 배추김치',
        '설렁탕 / 오징어볶음 / 브로콜리무침 / 총각김치',
        '알탕 / 제육볶음 / 시금치나물 / 배추김치',
        '',
        '',
      ].join('\n'),
      updatedAt: daysAgo(1),
    },
  ],

  siteRestaurants: [
    restaurant('rest-1', 'site-1', '오송한식뷔페', '충북 청주시 흥덕구 오송읍 오송생명로 122', '백반 / 찌개류', '9,000원', 1),
    restaurant('rest-2', 'site-1', '반도체식당', '충북 청주시 흥덕구 오송읍 연제리 44-2', '국밥 / 덮밥', '8,500원', 2),
    restaurant('rest-3', 'site-2', '판교기사식당', '경기 성남시 분당구 삼평동 682', '백반 / 제육', '10,000원', 1),
    restaurant('rest-4', 'site-3', '청북맛집', '경기 평택시 청북읍 어연리 210', '한식 / 부대찌개', '9,500원', 1),
  ],

  requestBoardPosts: [
    {
      id: 'req-1',
      requesterName: '공무팀 이서준',
      requestContent:
        '주간회의 화면에서 현장을 고를 때마다 목록 맨 위로 스크롤이 올라갑니다.\n현장이 30개가 넘어가니 매번 다시 내려야 해서 불편합니다.',
      responseContent: '선택한 현장 위치를 기억하도록 수정했습니다. 2026-07-15 배포분에 반영됐습니다.',
      completed: true,
      createdAt: daysAgo(21),
      updatedAt: daysAgo(9),
    },
    {
      id: 'req-2',
      requesterName: '안전관리팀',
      requestContent: '월간회의 캘린더를 A4 가로로 인쇄하면 마지막 주가 잘립니다. 인쇄 여백 조정 부탁드립니다.',
      responseContent: null,
      completed: false,
      createdAt: daysAgo(6),
      updatedAt: daysAgo(6),
    },
    {
      id: 'req-3',
      requesterName: '청주 반도체 현장',
      requestContent: '현장 현황에 공정율을 그래프로도 볼 수 있으면 좋겠습니다.',
      responseContent: null,
      completed: false,
      createdAt: daysAgo(2),
      updatedAt: daysAgo(2),
    },
  ],

  personnel: SEED_PERSONNEL,

  personnelCareers: [
    career('c-1', 'p-1', '청주대학교 기숙사 리모델링', '청주대학교', '철근콘크리트', '건축', '건축시공', '현장대리인', '총괄', 1,"정규직" as any,"보미건설"),
    career('c-2', 'p-1', '오송 바이오플랜트 신축', '(주)한빛바이오', '철골', '건축', '건축시공', '현장대리인', '총괄', 2,"정규직" as any,"보미건설"),
    career('c-3', 'p-1', '용인 반도체 클린룸 증축', '삼우전자', '철골철근콘크리트', '건축', '건축시공', '공사부장', '부분책임', 3,"정규직" as any,"보미건설"),
    career('c-4', 'p-2', '판교 데이터센터 신축', '케이티에스테이트', '철골', '건축', '건축시공', '공무팀장', '부분책임', 1,"정규직" as any,"보미건설"),
    career('c-5', 'p-2', '세종 청사 리모델링', '조달청', '철근콘크리트', '건축', '건축시공', '공무담당', '보조', 2,"정규직" as any,"보미건설"),
    career('c-6', 'p-3', '평택 지하차도 개설', '평택시청', '토공/구조물', '토목', '토목시공', '현장대리인', '총괄', 1,"정규직" as any,"보미건설"),
    career('c-7', 'p-3', '인천 항만 호안 보강', '인천항만공사', '항만', '토목', '항만시공', '공사과장', '부분책임', 2,"정규직" as any,"보미건설"),
    career('c-8', 'p-4', '대전 연구동 증축 설계', '한국기초과학연구원', '철근콘크리트', '건축', '건축설계', '설계담당', '보조', 1,"정규직" as any,"보미건설"),
    career('c-9', 'p-5', '오송 바이오플랜트 신축', '(주)한빛바이오', '철골', '안전', '건설안전', '안전관리자', '총괄', 1,"정규직" as any,"보미건설"),
    career('c-10', 'p-6', '판교 오피스 전기설비 개선', '판교테크노밸리자산운용', '전기', '전기', '전기공사', '전기팀장', '총괄', 1,"정규직" as any,"보미건설"),
    career('c-11', 'p-7', '청주 반도체 공장 기계설비', '(주)한빛반도체', '기계', '기계', '기계설비', '기계담당', '부분책임', 1,"정규직" as any,"보미건설"),
    career('c-12', 'p-8', '하노이 산업단지 조성', 'VN Industrial Park JSC', '토공', '건축', '건축시공', '공무담당', '보조', 1,"정규직" as any,"보미건설"),
  ],

  hqTaskItems: [
    hqTask('hq-1', CURRENT_WEEK.id, 'dept-2', '청주 반도체 공장 골조 3층 타설 완료, 4층 준비', 1),
    hqTask('hq-2', CURRENT_WEEK.id, 'dept-2', '평택 물류센터 토공 마무리 및 기초 검측', 2),
    hqTask('hq-3', CURRENT_WEEK.id, 'dept-3', '판교 오피스 설계변경 3차 정산 협의', 1),
    hqTask('hq-4', CURRENT_WEEK.id, 'dept-3', '하노이 현장 기성 청구 서류 검토', 2),
    hqTask('hq-5', CURRENT_WEEK.id, 'dept-5', '전 현장 하절기 폭염 대응 점검 (7/20~7/31)', 1),
    hqTask('hq-6', PREV_WEEK.id, 'dept-2', '청주 반도체 공장 골조 2층 타설 완료', 1),
    hqTask('hq-7', PREV_WEEK.id, 'dept-2', '평택 물류센터 착공 신고 접수', 2),
    hqTask('hq-8', PREV_WEEK.id, 'dept-3', '판교 오피스 설계변경 2차 협의', 1),
  ],

  siteTaskItems: [
    // 공정율·공사진행사항처럼 구조가 있는 분류는 content 가 JSON 이다 (siteTaskContent.ts 참조).
    siteTask('st-1', CURRENT_WEEK.id, 'site-1', '공정율', progressContent('62', '58', '자재 반입 지연으로 4일 만회 필요'), 1),
    siteTask('st-2', CURRENT_WEEK.id, 'site-1', '공사진행사항', detailContent('건축', '3층 슬래브 타설 완료, 4층 벽체 거푸집 진행'), 1),
    siteTask('st-3', CURRENT_WEEK.id, 'site-1', '공사진행사항', detailContent('기계', '지하 기계실 배관 슬리브 설치'), 2),
    siteTask('st-4', CURRENT_WEEK.id, 'site-1', '자재', detailContent('구매', '철근 SD400 D22 120톤 발주'), 1),
    siteTask('st-5', CURRENT_WEEK.id, 'site-1', '인원', '금주 투입 인원 평균 78명', 1),
    siteTask('st-6', CURRENT_WEEK.id, 'site-2', '공정율', progressContent('41', '44', '계획 대비 3% 선행'), 1),
    siteTask('st-7', CURRENT_WEEK.id, 'site-2', '설계변경', '지하 주차장 램프 구배 변경 승인 대기', 1),
    siteTask('st-8', CURRENT_WEEK.id, 'site-2', '대갑기성', detailContent('청구', '7월 기성 청구 완료'), 1),
    siteTask('st-9', CURRENT_WEEK.id, 'site-3', '공정율', progressContent('15', '15', ''), 1),
    // 준공 현장. 준공현장 화면이 읽는다.
    siteTask('st-10', CURRENT_WEEK.id, 'site-5', '기타', '하자보수 요청 2건 접수, 8/5까지 조치 예정', 1),
    siteTask('st-11', PREV_WEEK.id, 'site-1', '공정율', progressContent('58', '55', ''), 1),
    siteTask('st-12', PREV_WEEK.id, 'site-1', '공사진행사항', detailContent('건축', '2층 슬래브 타설 완료'), 1),
    siteTask('st-13', PREV_WEEK.id, 'site-2', '공정율', progressContent('37', '39', ''), 1),
  ],

  weeklyExecutiveNotes: [
    {
      id: 'note-hq-1',
      weekId: CURRENT_WEEK.id,
      noteType: 'hq',
      content: '하절기 안전관리에 각별히 유의할 것. 각 부서는 현장 지원 요청에 우선 대응 바랍니다.',
      updatedAt: daysAgo(1),
    },
    {
      id: 'note-site-1',
      weekId: CURRENT_WEEK.id,
      noteType: 'site',
      content: '공정 지연 현장은 만회 대책을 다음 주까지 서면 보고해 주세요.',
      updatedAt: daysAgo(1),
    },
  ],

  monthlyMeetingCategories: [
    { id: 'cat-1', name: '입찰', color: '#f97316', sortOrder: 1 },
    { id: 'cat-2', name: '내부회의(착공, 준공, 월간)', color: '#0ea5e9', sortOrder: 2 },
    { id: 'cat-3', name: '외부회의(PJT)', color: '#8b5cf6', sortOrder: 3 },
    { id: 'cat-4', name: '착공', color: '#22c55e', sortOrder: 4 },
    { id: 'cat-5', name: '준공', color: '#ef4444', sortOrder: 5 },
    { id: 'cat-6', name: '외부행사', color: '#eab308', sortOrder: 6 },
  ],

  monthlyMeetingEvents: [
    // 본사 — 반복 있는 것과 없는 것을 섞어 두어야 반복 처리 UI 를 확인할 수 있다.
    monthlyEvent('mm-1', 'hq', '월간 경영회의', thisMonthDay(5), '10:00', {
      categoryId: 'cat-2',
      departmentIds: ['dept-1', 'dept-2', 'dept-3'],
      recurrenceType: 'monthly',
    }),
    monthlyEvent('mm-2', 'hq', '주간 공무 점검', thisMonthDay(2), '09:30', {
      categoryId: 'cat-2',
      departmentIds: ['dept-3'],
      recurrenceType: 'weekly',
    }),
    monthlyEvent('mm-3', 'hq', '평택 물류센터 입찰 마감', thisMonthDay(14), '17:00', {
      categoryId: 'cat-1',
      departmentIds: ['dept-6'],
    }),
    monthlyEvent('mm-4', 'hq', '대전 연구동 준공식', thisMonthDay(22), '14:00', {
      categoryId: 'cat-5',
      departmentIds: ['dept-1', 'dept-2'],
    }),

    // 현장 회의일정
    monthlyEvent('mm-5', 'site', '청주 현장 정기회의', thisMonthDay(8), '10:00', {
      siteId: 'site-1',
      departmentIds: ['dept-2', 'dept-5'],
      recurrenceType: 'weekly',
    }),
    monthlyEvent('mm-6', 'site', '판교 현장 공정회의', thisMonthDay(11), '15:00', {
      siteId: 'site-2',
      departmentIds: ['dept-2'],
    }),

    // 현장(field) 스코프
    monthlyEvent('mm-7', 'field', '평택 안전 점검', thisMonthDay(9), '11:00', {
      siteId: 'site-3',
      departmentIds: ['dept-5'],
      recurrenceType: 'daily',
      recurrenceInterval: 7,
      recurrenceWeekdays: [3],
    }),
  ],

  monthlyMeetingExceptions: [],

  personnelAssignments: [
    assignment('pa-1', 'p-1', 'site', '청주 반도체 공장 신축', '현장대리인', '건축', 1),
    assignment('pa-2', 'p-7', 'site', '청주 반도체 공장 신축', '기계담당', '기계', 2),
    assignment('pa-3', 'p-2', 'department', '공무팀', '공무팀장', '건축', 1, true),
    assignment('pa-4', 'p-2', 'site', '판교 오피스 리모델링', '공무 담당', '건축', 1),
    assignment('pa-5', 'p-6', 'site', '성남 판교 오피스 리모델링', '현장대리인', '전기', 1),
    assignment('pa-6', 'p-3', 'site', '평택 물류센터 신축', '현장대리인', '토목', 1),
    assignment('pa-7', 'p-8', 'site', '하노이 산업단지 조성', '공무 담당', '건축', 1),
    assignment('pa-8', 'p-5', 'department', '안전관리팀', '안전보건관리책임자', '안전', 1, true),
    assignment('pa-9', 'p-4', 'department', '설계팀', '설계담당', '건축', 1),
  ],

  noticePosts: [
    {
      id: 'notice-1',
      title: '2026년 하절기 폭염 대응 지침 안내',
      content:
        '기상청 폭염 특보 발령 시 아래 기준으로 작업을 조정합니다.\n\n1. 체감온도 33℃ 이상 — 시간당 10분 이상 휴식\n2. 체감온도 35℃ 이상 — 14~17시 옥외작업 중지\n3. 각 현장은 그늘막·음용수·제빙기 상태를 매일 점검하고 안전관리팀에 보고',
      authorName: '안전관리팀',
      boardKey: 'news',
      category: 'info',
      attachmentName: '하절기_폭염대응지침.pdf',
      attachmentUrl: null,
      createdAt: daysAgo(3),
    },
    {
      id: 'notice-2',
      title: '평택 물류센터 신축공사 착공 안내',
      content: '평택 물류센터 신축공사가 2026-07-13 착공했습니다. 관련 부서는 착공 서류 제출을 완료해 주세요.',
      authorName: '공무팀',
      boardKey: 'news',
      category: 'starting',
      attachmentName: null,
      attachmentUrl: null,
      createdAt: daysAgo(11),
    },
    {
      id: 'notice-3',
      title: '전자계약 시스템 전환 안내 (8월 1일부터)',
      content: '8월 1일부터 신규 하도급 계약은 전자계약으로만 진행됩니다. 기존 진행 건은 종전 방식으로 마무리합니다.',
      authorName: '경영지원팀',
      boardKey: 'news',
      category: 'contract',
      attachmentName: null,
      attachmentUrl: null,
      createdAt: daysAgo(24),
    },
    {
      id: 'notice-4',
      title: '대전 연구동 증축공사 준공 보고',
      content: '대전 연구동 증축공사가 준공 처리되었습니다. 하자보수 접수는 공무팀으로 연락 바랍니다.',
      authorName: '공무팀',
      boardKey: 'news',
      category: 'completion',
      attachmentName: null,
      attachmentUrl: null,
      createdAt: daysAgo(38),
    },

    // ── 보미소식 ──────────────────────────────────────────────────────────
    //
    // 생일 안내 둘은 사람이 쓴 글이 아니라 `BirthdayNoticeScheduler` 가 만드는 모양을 그대로
    // 옮긴 것이다(작성자 「자동 안내」). 서버 없이 화면만 띄웠을 때도 자동 글이 어떻게 생겼는지
    // 보이라고 넣어 둔다 — 경조사와 나란히 놓고 봐야 한 탭에 섞어도 되는지 판단할 수 있다.
    // 경조사는 반대로 총무·경영지원이 「글쓰기」 로 직접 올리는 글이다.
    //
    // 이름은 위 인력 목 자료에서 빌려 왔지만 그 사람들의 생년월일과 맞춰 두지는 않았다 — 목 게시글은
    // 서식을 보여 주는 글이고, 실제 목록은 언제나 서버가 `hr_personnel` 을 읽어 만든다.
    {
      id: 'notice-5',
      title: '8월 28일 생일 안내',
      content: '· 본사 설계팀 최유진 님\n· 청주 반도체 공장 신축 오세영 님\n\n생일 축하합니다!',
      authorName: '자동 안내',
      boardKey: 'news',
      category: 'bomi',
      attachmentName: null,
      attachmentUrl: null,
      // 접힌 줄의 「축하 n」 배지가 어떻게 보이는지까지 확인하라고 넣어 둔다.
      commentCount: 4,
      createdAt: daysAgo(0),
    },
    {
      id: 'notice-6',
      title: '[결혼] 본사 공무팀 이서준 차장 결혼 안내',
      content:
        '본사 공무팀 이서준 차장이 결혼합니다.\n\n· 일시 : 2026년 9월 12일(토) 12시\n· 장소 : 서울 강남 ○○웨딩홀 3층 그랜드홀\n· 화환·경조금 문의 : 경영지원팀 (내선 210)\n\n축하의 마음 함께해 주시기 바랍니다.',
      authorName: '경영지원팀',
      boardKey: 'news',
      category: 'bomi',
      attachmentName: null,
      attachmentUrl: null,
      commentCount: 7,
      // 결혼은 몇 주 뒤에 만나도 축하할 일이라 글쓴이가 한 달을 골랐다.
      widgetDays: 30,
      // 「누구의 소식인가」 — 글쓴이가 주소록에서 고른 그때의 표기다.
      subjectPersonnelId: 'p-2',
      subjectName: '이서준',
      subjectDeptSite: '본사 공무팀',
      createdAt: daysAgo(2),
    },
    {
      id: 'notice-7',
      // 금요일 글이 주말 몫까지 안은 경우. 제목이 안는 날짜를 그대로 말한다.
      title: '8월 21일~8월 23일 생일 안내',
      content:
        '■ 8월 21일 (금)\n· 평택 물류센터 신축 박민수 님\n\n■ 8월 22일 (토)\n· 본사 공무팀 이서준 님\n\n생일 축하합니다!',
      authorName: '자동 안내',
      boardKey: 'news',
      category: 'bomi',
      attachmentName: null,
      attachmentUrl: null,
      createdAt: daysAgo(7),
    },
    {
      id: 'notice-8',
      title: '[부고] 본사 안전관리팀 정한결 부장 부친상',
      content:
        '본사 안전관리팀 정한결 부장의 부친께서 별세하셨습니다.\n\n· 빈소 : ○○병원 장례식장 5호실\n· 발인 : 2026년 8월 16일(일) 07시\n· 장지 : ○○추모공원\n\n삼가 고인의 명복을 빕니다.',
      authorName: '경영지원팀',
      boardKey: 'news',
      category: 'bomi',
      attachmentName: null,
      attachmentUrl: null,
      // 부고는 발인이 지나면 할 일이 끝난다 — 일주일이라 지금은 위젯에서 빠져 있다(글은 남는다).
      widgetDays: 7,
      subjectPersonnelId: 'p-5',
      subjectName: '정한결',
      subjectDeptSite: '본사 안전관리팀',
      createdAt: daysAgo(14),
    },
  ],

  // 자산 관련 공지는 보미소식이 아니라 자산관리 탭에서 본다(V64 로 실데이터도 옮겼다).
  assetNoticePosts: [
    {
      id: 'asset-notice-1',
      title: '노후 자산(노트북·모니터) 교체 신청 안내',
      content:
        '취득 5년이 지난 노트북·모니터는 교체 대상입니다.\n\n1. 부서별 대상 목록을 확인해 총무부로 회신\n2. 교체 자산은 전자결재 「자산 구매」 로 상신\n3. 반납 자산은 자산대장에서 폐기 처리 후 수거',
      authorName: '총무부',
      boardKey: 'asset',
      category: 'info',
      attachmentName: null,
      attachmentUrl: null,
      createdAt: daysAgo(6),
    },
    {
      id: 'asset-notice-2',
      title: '하반기 소모품 정기 불출 일정',
      content: '하반기 정기 불출은 매월 첫째 주 화요일에 진행합니다. 신청은 전주 금요일까지 마감합니다.',
      authorName: '총무부',
      boardKey: 'asset',
      category: 'planning',
      attachmentName: null,
      attachmentUrl: null,
      createdAt: daysAgo(19),
    },
  ],

  menuSettings: [],

  users: [
    user('u-1', 'admin@bomienc.co.kr', '김도현', '부장', 'super_admin', [
      { scopeType: 'site', scopeName: '청주 반도체 공장 신축', primary: true, showInPlacement: true, duty: '총괄소장' },
    ]),
    user('u-2', 'lee.sj@bomienc.co.kr', '이서준', '차장', 'admin', [
      { scopeType: 'department', scopeName: '공무팀', primary: true, showInPlacement: true, duty: '팀장' },
      { scopeType: 'site', scopeName: '성남 판교 오피스 리모델링', primary: false, showInPlacement: false, duty: null },
    ]),
    user('u-3', 'park.ms@bomienc.co.kr', '박민수', '과장', 'user', [
      { scopeType: 'site', scopeName: '평택 물류센터 신축', primary: true, showInPlacement: true, duty: '현장대리인' },
    ]),
    user('u-4', 'choi.yj@bomienc.co.kr', '최유진', '대리', 'user', [
      { scopeType: 'department', scopeName: '설계팀', primary: true, showInPlacement: true, duty: null },
    ]),
  ],

  constructionTrendPosts: [
    trend('ct-1', '국토부, 2026년 하반기 건설공사 표준품셈 개정 고시', 'news', '기계화 시공 확대에 따라 토공·포장 품셈이 조정되었습니다. 적용은 8월 1일 발주분부터입니다.', '대한건설신문', 2),
    trend('ct-2', '저탄소 콘크리트 국내 적용 사례 분석', 'journal', '고로슬래그 치환율별 압축강도 발현 특성과 현장 적용 시 유의점을 정리한 논문입니다.', '한국콘크리트학회', 9),
    trend('ct-3', '2026년 2분기 건설수주 동향', 'market', '민간 주택 부문이 전년 동기 대비 12% 감소한 반면 공공 토목은 8% 증가했습니다.', '건설산업연구원', 16),
    trend('ct-4', '스마트 안전장비 의무화 확대 검토', 'news', '수집기가 피드에서 가져온 기사입니다.', 'EBN 건설', 1),
  ],

  constructionTrendSources: [
    { id: 'cs-1', name: '대한건설신문', feedUrl: 'https://www.dnews.co.kr/rss', trendType: 'news', active: true, sortOrder: 1 },
    { id: 'cs-2', name: 'EBN 건설', feedUrl: 'https://www.ebn.co.kr/rss/construction', trendType: 'news', active: true, sortOrder: 2 },
    { id: 'cs-3', name: '건설산업연구원', feedUrl: 'https://www.cerik.re.kr/rss', trendType: 'market', active: false, sortOrder: 3 },
  ],

  internalGuidelinePosts: [
    guideline('ig-1', '하도급 계약 체결 지침', '계약', '공무팀', '2026-01-01', '설계변경에 따른 계약금액 조정 절차와 필요 서류를 정리합니다.', ['하도급', '계약', '설계변경'], {
      versionGroupKey: 'g-contract',
      versionLabel: 'v2.1',
      attachments: [{ name: '하도급계약_체크리스트.pdf', url: null }],
    }),
    guideline('ig-2', '하도급 계약 체결 지침', '계약', '공무팀', '2025-03-01', '2026년 개정 전 버전입니다.', ['하도급', '계약'], {
      versionGroupKey: 'g-contract',
      versionLabel: 'v2.0',
      effectiveEndDate: '2025-12-31',
    }),
    guideline('ig-3', '현장 안전관리 기본 지침', '안전', '안전관리팀', '2026-02-01', '작업 전 안전점검(TBM) 절차와 위험성평가 주기를 규정합니다.', ['안전', 'TBM', '위험성평가'], {
      attachments: [{ name: '위험성평가_양식.xlsx', url: null }],
    }),
    guideline('ig-4', '출장 및 경비 처리 지침', '총무', '경영지원팀', '2026-01-01', '국내·해외 출장비 기준과 정산 기한을 안내합니다.', ['출장', '경비', '정산'], {
      externalUrlName: '경비 정산 시스템',
      externalUrl: 'https://example.com/expense',
    }),
  ],

  bomiPerformancePosts: [
    {
      id: 'bp-1',
      sourceSiteId: 'site-5',
      siteName: '대전 연구동 증축',
      siteCategory: '국내진행현장',
      status: '준공',
      contractStartDate: '2024-03-01',
      contractEndDate: '2026-02-28',
      actualStartDate: '2024-03-11',
      scheduledCompletionDate: '2026-02-20',
      scale: '공사금액 14,300백만원 · 층수 지상 6층 · 연면적 12,600㎡',
      contractAmount: '14,300백만원',
      floorCount: '지상 6층',
      grossAreaSqm: 12600,
      facilityType: '연구시설',
      siteAddress: '대전광역시 유성구 과학로',
      clientName: '한국기초과학연구원',
      supervisorName: '(주)한길엔지니어링',
      designerName: '(주)정림건축',
      methodText: '탑다운 공법 적용으로 도심 인접 부지의 굴착 소음·진동을 줄였습니다.',
      journalPdfName: '대전연구동_공사지.pdf',
      journalPdfUrl: null,
      createdAt: daysAgo(40),
    },
  ],

  employeeNewsItems: [
    employeeNews('en-1', 'join', '문가람', '사원', '하노이 산업단지 조성', thisMonthDay(2)),
    employeeNews('en-2', 'join', '서지호', '대리', '공무팀', thisMonthDay(2)),
    employeeNews('en-3', 'join', '임채원', '사원', '설계팀', thisMonthDay(16)),
    employeeNews('en-4', 'leave', '강태식', '과장', '평택 물류센터 신축', thisMonthDay(10)),
    employeeNews('en-5', 'leave', '노현주', '대리', '경영지원팀', thisMonthDay(20)),
  ],

  technicalCasePosts: [
    techPost('tc-1', '건축', '청주 반도체 공장 신축', '외벽 알루미늄 패널 시공 시 단열재 이격 방지 상세 적용', 12, {
      caseType: '우수사례',
      attachmentName: '외벽패널_상세도.pdf',
      attachmentUrl: 'https://drive.google.com/file/d/example-1/view',
    }),
    techPost('tc-2', '기계', '성남 판교 오피스 리모델링', '기존 덕트 재사용 검토로 자재비 절감 (약 4,200만원)', 26, {
      caseType: 'VE개선사례',
    }),
    techPost('tc-3', '토목', '평택 물류센터 신축', '우수 유입으로 터파기 사면 일부 붕괴 — 배수계획 선행 필요', 40, {
      caseType: '미흡사례',
    }),
    techPost('tc-4', '전기', '대전 연구동 증축', '수배전반 반입 동선 사전 검토 미흡으로 크레인 재배치 발생', 55, {
      caseType: '미흡사례',
    }),
  ],

  newTechnologyPosts: [
    techPost('nt-1', '건축', '청주 반도체 공장 신축', 'PC 슬래브 공법 적용 검토 — 공기 단축 약 3주 예상', 8, {
      attachmentName: 'PC슬래브_기술자료.pdf',
      attachmentUrl: 'https://drive.google.com/file/d/example-2/view',
    }),
    techPost('nt-2', '구조', null, '저탄소 콘크리트(고로슬래그 50% 치환) 국내 적용 사례 정리', 19, {}),
    techPost('nt-3', '기타', '평택 물류센터 신축', 'AI CCTV 기반 안전모 미착용 감지 시스템 시범 적용', 33, {}),
  ],
};

// ── 시드 헬퍼 ────────────────────────────────────────────────────────────────

function department(id: string, name: string, sortOrder: number, active = true, showInWork = true): Department {
  return { id, name, sortOrder, active, showInWork, parentId: null, chartSlot: null, chartDx: 0, chartDy: 0 };
}

function site(
  id: string,
  name: string,
  siteCategory: Site['siteCategory'],
  status: Site['status'],
  sortOrder: number,
  extra: Partial<Site> = {},
): Site {
  return {
    id,
    name,
    siteCategory,
    status,
    sortOrder,
    active: true,
    contractPeriod: null,
    contractStartDate: null,
    contractEndDate: null,
    actualStartDate: null,
    scheduledCompletionDate: null,
    targetCompletionDate: null,
    scale: null,
    contractAmount: null,
    floorCount: null,
    grossAreaSqm: null,
    facilityType: null,
    completionServiceAlert: false,
    meetingColor: null,
    siteAddress: null,
    clientName: null,
    supervisorName: null,
    designerName: null,
    nasLinkUrl: null,
    contractSummaryPdfName: null,
    contractSummaryPdfUrl: null,
    representativeImageUrl: null,
    specialArea: false,
    ...extra,
  };
}

function restaurant(
  id: string,
  siteId: string,
  restaurantName: string,
  address: string,
  menuText: string,
  priceText: string,
  sortOrder: number,
): SiteRestaurant {
  return { id, siteId, restaurantName, address, menuText, priceText, sortOrder, createdAt: daysAgo(30) };
}

function person(
  id: string,
  name: string,
  rank: string,
  position: string,
  field: string,
  deptSite: string,
  birthDate: string,
  joinDate: string,
  grade: string,
): Personnel {
  return {
    id,
    name,
    rank,
    position,
    field,
    deptSite,
    birthDate,
    joinDate,
    grade,
    leaveDate: null,
    accountLinked: true,
    phone: '010-0000-0000',
    extension: null,
    email: `${id}@bomienc.co.kr`,
    score: null,
    seatFloor: null,
    seatZone: null,
    seatNo: null,
    directoryHidden: false,
  };
}

function trend(
  id: string,
  title: string,
  trendType: ConstructionTrendPost['trendType'],
  content: string,
  sourceName: string,
  createdDaysAgo: number,
): ConstructionTrendPost {
  return {
    id,
    title,
    trendType,
    content,
    sourceName,
    sourceUrl: 'https://example.com/article',
    publishedAt: daysAgo(createdDaysAgo).slice(0, 10),
    createdAt: daysAgo(createdDaysAgo),
  };
}

function guideline(
  id: string,
  title: string,
  guidelineType: string,
  departmentName: string,
  effectiveStartDate: string,
  content: string,
  keywords: string[],
  extra: Partial<InternalGuidelinePost> = {},
): InternalGuidelinePost {
  return {
    id,
    title,
    guidelineType,
    departmentName,
    effectiveStartDate,
    effectiveEndDate: null,
    revisedAt: effectiveStartDate,
    changeNote: null,
    content,
    keywords,
    attachments: [],
    externalUrlName: null,
    externalUrl: null,
    versionGroupKey: null,
    versionLabel: null,
    createdAt: daysAgo(30),
    updatedAt: daysAgo(30),
    ...extra,
  };
}

function user(
  id: string,
  email: string,
  name: string,
  rank: string,
  userType: UserRecord['userType'],
  scopes: UserRecord['scopes'],
): UserRecord {
  // 메뉴 권한은 `UserRecord` 가 아니라 세션에 실린다(`Session.menuPermissions`). 목록이 읽는 것은
  // 「먼저 열기」 수뿐이고(V264), 흉내 자료에는 미리보기를 두지 않는다 — 오픈 전에만 있는
  // 임시 상태가 기본값처럼 보이면 안 된다.
  return { id, email, name, rank, userType, active: true, mustChangePassword: false, scopes, menuPreviewCount: 0 };
}

function employeeNews(
  id: string,
  newsType: EmployeeNewsItem['newsType'],
  employeeName: string,
  rank: string,
  deptSite: string,
  newsDate: string,
): EmployeeNewsItem {
  return { id, newsType, employeeName, rank, deptSite, newsDate, email: `${id}@bomienc.co.kr` };
}

function techPost(
  id: string,
  workType: TechnicalPost['workType'],
  siteName: string | null,
  content: string,
  createdDaysAgo: number,
  extra: Partial<TechnicalPost> = {},
): TechnicalPost {
  return {
    id,
    workType,
    siteName,
    content,
    caseType: null,
    attachmentName: null,
    attachmentUrl: null,
    deleteRequested: false,
    createdAt: daysAgo(createdDaysAgo),
    ...extra,
  };
}

function assignment(
  id: string,
  personnelId: string,
  assignmentType: PersonnelAssignment['assignmentType'],
  assignmentName: string,
  roleTitle: string,
  fieldName: string,
  sortOrder: number,
  teamLead = false,
): PersonnelAssignment {
  // 계약상 현장대리인(V401) — 목에서는 담당이 그 글자인 줄이 곧 그 사람이다.
  return { id, personnelId, assignmentType, assignmentName, roleTitle, fieldName, sortOrder, teamLead,
    siteAgent: roleTitle.includes('현장대리') };
}

/** 이번 달 n일. 달력을 열면 이번 달에 일정이 보이도록 오늘 기준으로 만든다. */
function thisMonthDay(day: number): string {
  const today = new Date();
  return `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
}

function monthlyEvent(
  id: string,
  scope: MonthlyMeetingEvent['scope'],
  title: string,
  eventDate: string,
  eventTime: string | null,
  extra: Partial<MonthlyMeetingEvent> = {},
): MonthlyMeetingEvent {
  return {
    id,
    scope,
    title,
    eventDate,
    eventTime,
    eventEndTime: null,
    categoryId: null,
    siteId: null,
    notes: null,
    location: null,
    roomAssetId: null,
    notifyAttendees: true,
    departmentIds: [],
    attendeePersonnelIds: [],
    recurrenceType: 'none',
    recurrenceInterval: 1,
    recurrenceWeekdays: null,
    recurrenceUntil: null,
    ...extra,
  };
}

function hqTask(id: string, weekId: string, departmentId: string, content: string, sortOrder: number): HqTaskItem {
  return { id, weekId, departmentId, taskType: 'curr_week', content, sortOrder, scheduleDate: null };
}

function siteTask(
  id: string,
  weekId: string,
  siteId: string,
  category: SiteTaskCategory,
  content: string,
  sortOrder: number,
): SiteTaskItem {
  return { id, weekId, siteId, departmentId: null, category, content, sortOrder, scheduleDate: null };
}

/** 공정율 항목의 content. 실제 화면이 쓰는 인코딩과 같아야 하므로 모양을 맞춰 둔다. */
function progressContent(plan: string, actual: string, comment: string): string {
  return JSON.stringify({ plan, actual, comment });
}

/** 하위 구분이 있는 분류의 content. */
function detailContent(sub: string, text: string): string {
  return JSON.stringify({ sub, text });
}

function career(
  id: string,
  personnelId: string,
  projectName: string,
  client: string,
  structure: string,
  jobField: string,
  specialtyField: string,
  position: string,
  responsibility: string,
  sortOrder: number,
  workType: string,
  company: string
): PersonnelCareer {
  return {
    id,
    personnelId,
    projectName,
    client,
    structure,
    jobField,
    specialtyField,
    position,
    responsibility,
    sortOrder,
    projectStart: null,
    projectEnd: null,
    projectOverview: null,
    method: null,
    techFusion: null,
    duties: null,
    // 새로 적은 경력은 아직 협회에 낸 적이 없는 것이 맞다 — 서버 기본값과 같다.
    reportStatus: 'UNREPORTED',
    reportedDate: null,
    recognizedDays: null,
    reportNote: null,
    workType,
    company
  };
}
